import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogBody,
  DialogActions,
} from "../dialog";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "../alert";
import { Button } from "../button";
import { Badge } from "../badge";
import { Textarea } from "../textarea";
import { useNavigate } from "react-router-dom";
import {
  CalendarDaysIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  TagIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  XMarkIcon,
  TrashIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { getClashingEvents, ClashWarning } from "./clashes";
import EspaciosPicker from "../shared/EspaciosPicker";
import { TIPOS_BORRABLES } from "./utils";

const ACCENT = "#1b4d2e";

const EVENT_TYPE_CONFIG = {
  scouting: { label: "Scouting", badge: "orange" },
  event: { label: "Evento", badge: "emerald" },
  reunion: { label: "Reunión", badge: "blue" },
  bloqueo: { label: "Bloqueo", badge: "red" },
};

function getFieldLabel(key, eventType) {
  const labels = {
    bloqueo: {
      nombre: "Razón del Bloqueo",
      startTime: "Inicio",
      endTime: "Fin",
      notes: "Notas",
    },
    event: {
      nombre: "Nombre del Evento",
      startTime: "Inicio del Bloqueo",
      inicioMontaje: "Inicio del Montaje",
      inicioEvento: "Inicio del Evento",
      finEvento: "Fin del Evento",
      finMontaje: "Fin del Desmontaje",
      endTime: "Fin del Bloqueo",
      cliente: "Cliente",
      telefono: "Teléfono",
      correo: "Correo",
      precioAcordado: "Precio Acordado",
      notes: "Notas",
    },
    reunion: {
      nombre: "Nombre de Reunión",
      startTime: "Inicio",
      endTime: "Fin",
      contacto: "Nombre de Contacto",
      telefono: "Teléfono de Contacto",
      notes: "Notas",
    },
    scouting: {
      nombre: "Nombre del Scouting",
      startTime: "Inicio",
      endTime: "Fin",
      contacto: "Nombre Contacto",
      telefono: "Teléfono Contacto",
      correo: "Correo Contacto",
      notes: "Notas",
    },
  };
  return labels[eventType]?.[key] || key;
}

function getEventFields(eventType) {
  switch (eventType) {
    case "bloqueo":
      return ["startTime", "endTime"];
    case "event":
      return [
        "startTime",
        "inicioMontaje",
        "inicioEvento",
        "finEvento",
        "finMontaje",
        "endTime",
        "cliente",
        "telefono",
        "correo",
        "precioAcordado",
      ];
    case "reunion":
      return ["startTime", "endTime", "contacto", "telefono"];
    case "scouting":
      return ["startTime", "endTime", "contacto", "telefono", "correo"];
    default:
      return [];
  }
}

function formatEventValue(key, value, timeZone, formatEventDate) {
  if (!value) return null;
  if (key.includes("Time") || key.includes("inicio") || key.includes("fin")) {
    let dateObj = value;
    if (value.toDate) {
      dateObj = value.toDate();
    } else if (typeof value === "object" && value.seconds) {
      dateObj = new Date(value.seconds * 1000);
    } else if (typeof value === "string") {
      dateObj = new Date(value);
    }
    if (formatEventDate) {
      return formatEventDate(dateObj, timeZone);
    }
    return dateObj instanceof Date && !isNaN(dateObj)
      ? dateObj.toLocaleString()
      : value.toString();
  }
  if (key === "precioAcordado") {
    return "$ " + Number(value).toLocaleString("es-MX");
  }
  return value;
}

function getFieldIcon(key) {
  if (key.includes("Time") || key.includes("inicio") || key.includes("fin"))
    return <ClockIcon className="h-4 w-4" />;
  if (key === "correo" || key === "correoContacto")
    return <EnvelopeIcon className="h-4 w-4" />;
  if (key === "telefono" || key === "telefonoContacto")
    return <PhoneIcon className="h-4 w-4" />;
  if (key === "cliente" || key === "contacto")
    return <UserIcon className="h-4 w-4" />;
  if (key === "precioAcordado")
    return <CurrencyDollarIcon className="h-4 w-4" />;
  return <TagIcon className="h-4 w-4" />;
}

function isDateField(key) {
  return key.includes("Time") || key.includes("inicio") || key.includes("fin");
}

const EventDialog = ({
  isOpen,
  onClose,
  selectedEvent,
  editName,
  setEditName,
  editNotes,
  setEditNotes,
  handleSaveEvent,
  // Solo scouting, reuniones y bloqueos se borran; un evento se cancela.
  handleDeleteEvent,
  timeZone,
  formatEventDate,
  isViewer = false,
  // Visor e inversionista: sin editar ni eliminar. isViewer además oculta
  // precio acordado y notas (el inversionista sí los ve).
  soloLectura = false,
  espaciosActivos = [],
  espaciosPorId = {},
  editEspacios = [],
  setEditEspacios = () => {},
  existingEvents = [],
  permiteEventosSimultaneos = true,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showClashConfirm, setShowClashConfirm] = useState(false);

  // Reset editing state when the dialog opens/closes or event changes
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
    }
  }, [isOpen, selectedEvent?.id]);

  if (!selectedEvent) return null;

  const eventType = selectedEvent.extendedProps.eventType;
  const config = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.event;
  const fields = getEventFields(eventType);

  const dateFields = fields.filter((f) => isDateField(f));
  const detailFields = fields.filter((f) => !isDateField(f));

  const originalName = selectedEvent.extendedProps.nombre || "";
  const originalNotes = selectedEvent.extendedProps.notes || "";
  const originalEspacios = selectedEvent.extendedProps.espaciosIds || [];
  const espaciosChanged =
    JSON.stringify([...originalEspacios].sort()) !==
    JSON.stringify([...editEspacios].sort());
  const hasChanges =
    editName !== originalName || editNotes !== originalNotes || espaciosChanged;

  const nombreEspacio = (id) => espaciosPorId[id]?.nombre || id;
  // En edición se ofrecen los activos + los que el evento ya tenía (aunque
  // hoy estén inactivos o borrados), para poder quitarlos.
  const opcionesEspacios = [
    ...espaciosActivos.map((e) => e.id),
    ...originalEspacios.filter((id) => !espaciosActivos.some((e) => e.id === id)),
  ];
  const muestraEspacios = espaciosActivos.length > 0 || originalEspacios.length > 0;

  // Cruces de la nueva selección de espacios contra el resto del calendario.
  // Las fechas no se editan en este diálogo, así que solo hay algo nuevo que
  // validar cuando cambió la selección; el propio evento se excluye.
  const clashingEvents =
    isEditing && espaciosChanged
      ? getClashingEvents({
          start: selectedEvent.start,
          end: selectedEvent.end,
          espaciosIds: editEspacios,
          excludeId: selectedEvent.id,
          existingEvents,
          espaciosPorId,
          permiteEventosSimultaneos,
        })
      : [];

  const handleClose = () => {
    if (isEditing && hasChanges) {
      setShowDiscardConfirm(true);
    } else {
      setIsEditing(false);
      onClose();
    }
  };

  const handleDiscard = () => {
    setEditName(originalName);
    setEditNotes(originalNotes);
    setEditEspacios(originalEspacios);
    setIsEditing(false);
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleSave = () => {
    if (clashingEvents.length > 0) {
      setShowClashConfirm(true);
      return;
    }
    handleSaveEvent();
    setIsEditing(false);
  };

  const handleConfirmSaveClash = () => {
    setShowClashConfirm(false);
    handleSaveEvent();
    setIsEditing(false);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose}>
        {/* Header — flush with dialog edges */}
        <div
          className="rounded-t-[calc(1.5rem-1px)] sm:rounded-t-[calc(1rem-1px)] px-[--gutter] pt-6 pb-5 -mx-[--gutter] -mt-[--gutter]"
          style={{ backgroundColor: ACCENT + "0d" }}
        >
          <div className="flex items-start gap-3 pr-6">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <CalendarDaysIcon
                className="h-5 w-5"
                style={{ color: ACCENT }}
              />
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={getFieldLabel("nombre", eventType)}
                  className="w-full bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-1.5 text-lg font-semibold text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                />
              ) : (
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white truncate">
                  {selectedEvent.extendedProps.nombre || (
                    <span className="italic text-zinc-400">Sin nombre</span>
                  )}
                </h2>
              )}
              <div className="mt-1.5 flex items-center gap-2">
                <Badge color={config.badge}>{config.label}</Badge>
                {selectedEvent.extendedProps?.status === "cancelado" && (
                  <Badge color="red">Cancelado</Badge>
                )}
              </div>
            </div>
            {selectedEvent.extendedProps?.status !== "cancelado" && !soloLectura && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="mt-1 rounded-lg p-1.5 transition hover:bg-zinc-200/60 dark:hover:bg-zinc-700/60"
                title={isEditing ? "Cancelar edición" : "Editar"}
              >
                {isEditing ? (
                  <XMarkIcon className="h-5 w-5 text-zinc-500" />
                ) : (
                  <PencilSquareIcon className="h-5 w-5 text-zinc-500" />
                )}
              </button>
            )}
          </div>
        </div>

        <DialogBody>
          {/* Date/time section */}
          {dateFields.length > 0 && (
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {dateFields.map((key) => {
                  const value = formatEventValue(
                    key,
                    selectedEvent.extendedProps[key],
                    timeZone,
                    formatEventDate
                  );
                  if (!value) return null;
                  return (
                    <div key={key} className="flex items-start gap-2.5">
                      <div
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                        style={{ backgroundColor: ACCENT + "1a" }}
                      >
                        <ClockIcon
                          className="h-3.5 w-3.5"
                          style={{ color: ACCENT }}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                          {getFieldLabel(key, eventType)}
                        </div>
                        <div className="text-sm font-medium text-zinc-900 dark:text-white">
                          {value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Espacios que ocupa el evento */}
          {muestraEspacios && (
            <div className="mt-4 rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Squares2X2Icon className="h-4 w-4" style={{ color: ACCENT }} />
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Espacios
                </span>
              </div>
              {isEditing ? (
                <>
                  <EspaciosPicker
                    opciones={opcionesEspacios.map((id) => ({
                      id,
                      nombre: nombreEspacio(id),
                    }))}
                    value={editEspacios}
                    onChange={setEditEspacios}
                  />
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {editEspacios.length === 0
                      ? "Sin espacios seleccionados, el evento bloquea todo el venue."
                      : "Al guardar se recalcula qué bloquea el evento con la configuración actual de espacios."}
                  </p>
                  {clashingEvents.length > 0 && (
                    <ClashWarning
                      events={clashingEvents}
                      titulo="Con estos espacios, el evento se cruza con:"
                    />
                  )}
                </>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {originalEspacios.length > 0 ? (
                    originalEspacios.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                      >
                        {nombreEspacio(id)}
                      </span>
                    ))
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-zinc-200/70 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
                      Todo el venue
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Detail fields */}
          {detailFields.length > 0 && (
            <div className="mt-4 space-y-3">
              {detailFields.map((key) => {
                if (isViewer && key === "precioAcordado") return null;
                const value = formatEventValue(
                  key,
                  selectedEvent.extendedProps[key],
                  timeZone,
                  formatEventDate
                );
                if (!value) return null;
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-white dark:bg-zinc-800/30 px-4 py-3"
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: ACCENT + "1a" }}
                    >
                      <span style={{ color: ACCENT }}>
                        {getFieldIcon(key)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                        {getFieldLabel(key, eventType)}
                      </div>
                      <div className="text-sm text-zinc-900 dark:text-white">
                        {value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Notes section (hidden for viewers) */}
          {!isViewer && <div className="mt-4 rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <DocumentTextIcon
                className="h-4 w-4"
                style={{ color: ACCENT }}
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Notas
              </span>
            </div>
            {isEditing ? (
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Agregar notas..."
                rows={3}
              />
            ) : (
              <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
                {selectedEvent.extendedProps.notes || (
                  <span className="italic text-zinc-400">Sin notas</span>
                )}
              </p>
            )}
          </div>}
        </DialogBody>

        <DialogActions>
          {TIPOS_BORRABLES.includes(eventType) && !soloLectura && handleDeleteEvent && (
            <Button onClick={handleDeleteEvent} color="red">
              <TrashIcon className="h-4 w-4" />
              Eliminar
            </Button>
          )}
          <div className="flex-1" />
          <Button onClick={handleClose} outline>
            Cerrar
          </Button>
          {isEditing && selectedEvent.extendedProps?.status !== "cancelado" && (
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.backgroundColor = "#164026";
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.backgroundColor = ACCENT;
              }}
            >
              Guardar
            </button>
          )}
          {!isEditing && (
            <button
              onClick={() => navigate(`/dashboard/eventos/${selectedEvent.id}`)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-4 py-2 text-sm font-semibold text-zinc-900 dark:text-white shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-700"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Ver detalles
            </button>
          )}
        </DialogActions>
      </Dialog>

      {/* Discard changes confirmation */}
      <Alert
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
      >
        <AlertTitle>Cambios sin guardar</AlertTitle>
        <AlertDescription>
          Tienes cambios sin guardar. ¿Deseas descartarlos?
        </AlertDescription>
        <AlertActions>
          <Button onClick={() => setShowDiscardConfirm(false)} outline>
            Seguir editando
          </Button>
          <Button onClick={handleDiscard} color="red">
            Descartar
          </Button>
        </AlertActions>
      </Alert>

      {/* Clash confirmation — mismo patrón que al crear */}
      <Alert open={showClashConfirm} onClose={() => setShowClashConfirm(false)}>
        <AlertTitle>Cruce de espacios</AlertTitle>
        <AlertDescription>
          Con los espacios seleccionados, el evento se cruza con otro evento o
          bloqueo existente. ¿Deseas guardar de todos modos?
        </AlertDescription>
        <AlertActions>
          <Button outline onClick={() => setShowClashConfirm(false)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmSaveClash}>
            Guardar de todos modos
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
};

export default EventDialog;
